import { ETypes } from "@/constants/gaoding";
import { PRODUCT_TYPE, SOLUTION_VERSION } from "@/constants/gaodingVip";
import { refreshVips } from "@/context/store";
import { goGdMerge } from "@/pageComponents/GdMerge";
import { getCurrentOrg } from "@/services";
import reporter from "@/tracker/report/reporter";
import getPageData from "@/tracker/shared/getPageData";
import { getPage } from "@/tracker/shared/global";
import { isGdMerged } from "@/utils/gaoding";
import { getPageEnterRecord } from "@/utils/hooks/usePageEnterRecord";
import { gaodingEditHost } from "@/utils/host";
import { openUdesk } from "@/utils/initUdesk";
import { checkLogin } from "@/utils/open";
import { PRESETS_CONDITION, stewardInstance } from "@/utils/steward";
import { ESeatTypes, ETradeTypes } from "@gdesign/iframe-modal";
import { isNumber } from "lodash";
import HBMessage from "../HBMessage";
import HBInfoDialog from "../HBModal/info";

function getTrackerData(option?: {
  source?: string;
  source_location?: string;
  search_word?: string;
  source_pin_id?: string | number;
  is_copyright_material?: number;
}): {
  source?: string;
  source_location?: string;
  page_name?: string;
  source_page?: string;
  page?: string;
} {
  const page = getPageData();

  return {
    ...option,
    page_name: page.page_nm,
    source:
      option?.source ||
      page.page_nm + (page.sub_page_nm ? `_${page.sub_page_nm}` : ""),
    source_location: option?.source_location,
    page: encodeURIComponent(location.href),
  };
}

/**支付弹窗关闭埋点 */
function closeReport(option = {}) {
  const page = getPage();

  reporter({
    ...option,
    ...page,
    event_id: 39008,
    event_name: "button_click",
    button_name: "关闭支付弹窗",
    button_click_type: "click",
  });
}

function buyVipVisitReport(option = {}) {
  const page = getPage();
  reporter({
    ...option,
    ...page,
    event_id: 39012,
    event_name: "dialog_expose",
    title: "支付弹窗加载",
  });
}

const commonConfig = {
  theme: "HB",
  createTeam: false,
  qrcode:
    "https://grocery-cdn.huaban.com/file/56e332e0-d470-11ee-82f6-373373912826.png",
};

async function mergeCheck(props, user) {
  if (isGdMerged(user)) {
    return true;
  }

  // selectSkuId是唤起融合弹窗的参数，表明是通过拉起一体化购买弹窗的链接，需要走融合流程
  if (props?.isGdDialog || isNumber(props.selectSkuId)) {
    const result = await goGdMerge({
      showMessage: false,
    });
    if (result) {
      return true;
    }
  }
}

const isBigCompany = async (
  props: {
    afterClose?: () => void;
  } = {}
) => {
  const info = await getCurrentOrg();

  if (info.product?.base_product_type === PRODUCT_TYPE.FLAGSHIP) {
    HBInfoDialog.open({
      title: "提示",
      content:
        new Date(info.expire_date).getTime() < Date.now()
          ? "您当前企业会员已过期，请联系客服续费"
          : "您的组织当前是企业版，无法购买素材会员，请联系客服进行购买",
      okText: "联系客服",
      afterClose: () => {
        props?.afterClose?.();
      },
      onOk: () => {
        openUdesk();
      },
      "data-gd-expose": "dialog_expose",
      "data-dialog-title": "企业版过期续费提示",
    });
    return true;
  }
};

/**
 * MARK: 购买弹窗
 */
export const BuyVipDialog = {
  open: async (props: any = {}) => {
    const user = await checkLogin();

    if (!user?.user_id) {
      return;
    }

    buyVipVisitReport({
      source: props.source,
    });

    const isMerged = await mergeCheck(props, user);

    if (!isMerged) {
      const { BuyVipDialog: oldBuy } = await import("./buy");
      return oldBuy.open({
        ...props,
        afterClose: () => {
          props?.afterClose?.();
          // @ts-ignore
          closeReport({
            button_source: props.source,
          });
        },
      });
    }

    // 融合用户流程
    if (await isBigCompany(props)) {
      return;
    }

    const { useBusinessModal } = await import("@gdesign/iframe-modal");

    const orgClassMap = {
      personal: "personal",
      company: "org",
    };

    if (location.hash) {
      location.hash = "";
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { open } = useBusinessModal({
      baseUrl: "https://" + gaodingEditHost,
      params: {
        type: props.buyType || "Vip",
        seatType: props.seatType || "gdmaterial",
        org_class: orgClassMap[props.type],
        sku_id: props.selectSkuId,
        config: {
          ...commonConfig,
          downloadClickCount: props.downloadClickCount,
          materialVisitCount: getPageEnterRecord(
            PRESETS_CONDITION.HUABAN_MATERIAL_DETAILS_ENTER_TIMES
          ),
        },
      },
      trackerData: getTrackerData({
        source: props.source,
        source_location: props.source_location,
        source_pin_id: props.pinId,
        search_word: props.searchWord,
        is_copyright_material: props.isCopyrightMaterial,
      }),
      lifecycle: {
        onComplete() {
          refreshVips();
          // @ts-ignore
          props.onOk?.();
        },
        onClose() {
          // @ts-ignore
          props.afterClose?.();
          stewardInstance.trigerAwaitInit(
            PRESETS_CONDITION.TODAY_CLOSE_PAY_DIALOG_TIMES,
            {}
          );
          closeReport({
            button_source: props.source,
          });

          // history.go(-1);
          // 同步不生效，放异步执行
          setTimeout(() => {
            history.go(-1);
          }, 0);
        },
      },
    });
    open();
    return true;
  },
};

/**
 * MARK: 增加下载次数弹框
 */
export const AdditionGoodDialog = {
  open: async (props: any = {}) => {
    const user = await checkLogin();

    if (!user.user_id) {
      return;
    }
    const isMerged = await mergeCheck(props, user);

    if (!isMerged) {
      const { AdditionGoodDialog: oldBuy } = await import("./buy");
      return oldBuy.open(props);
    }

    if (await isBigCompany()) {
      return;
    }

    const { useBusinessModal } = await import("@gdesign/iframe-modal");
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { open } = useBusinessModal({
      baseUrl: "https://" + gaodingEditHost,

      params: {
        type: "Trade",
        tradeType: "MATERIAL_DOWNLOAD",
        seatType: "gdmaterial", // 花瓣席位
        config: commonConfig,
      },
      trackerData: getTrackerData(),
      lifecycle: {
        onComplete() {
          // @ts-ignore
          props.onOk?.();
        },
        onClose() {
          // @ts-ignore
          props.afterClose?.();
          setTimeout(() => {
            history.go(-1);
          }, 0);
        },
      },
    });
    open();
  },
};

/**
 * MARK: 扩展授权弹框
 */
export const VipAuthDialog = {
  open: async (props = {}) => {
    const user = await checkLogin();

    if (!user.user_id) {
      return;
    }

    const isMerged = await mergeCheck(props, user);

    if (!isMerged) {
      const { VipAuthDialog: oldBuy } = await import("./buy");
      return oldBuy.open(props);
    }

    if (await isBigCompany()) {
      return;
    }

    const { useBusinessModal } = await import("@gdesign/iframe-modal");
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { open } = useBusinessModal({
      baseUrl: "https://" + gaodingEditHost,
      params: {
        type: "Vip",
        seatType: "gdmaterial", // 花瓣席位
        version_id: SOLUTION_VERSION.CORPORATE_PRO,
        config: commonConfig,
      },
      trackerData: getTrackerData(),
      lifecycle: {
        onComplete() {
          // @ts-ignore
          props.onOk?.();
        },
        onClose() {
          // @ts-ignore
          props.afterClose?.();
          setTimeout(() => {
            history.go(-1);
          }, 0);
        },
      },
    });
    open();
  },
};

/**
 * MARK: 授权主体弹框
 */
export const AddSubjectCertDialog = {
  open: async (props = {}) => {
    const user = await checkLogin();

    if (!user.user_id) {
      return;
    }

    const isMerged = await mergeCheck(props, user);

    if (!isMerged) {
      const { AddSubjectCertDialog: oldBuy } = await import("./buy");
      return oldBuy.open(props);
    }

    if (await isBigCompany()) {
      return;
    }

    const { useBusinessModal } = await import("@gdesign/iframe-modal");
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { open } = useBusinessModal({
      baseUrl: "https://" + gaodingEditHost,
      params: {
        type: "Trade",
        tradeType: "MATERIAL_CERTS",
        config: commonConfig,
      },

      trackerData: getTrackerData(),
      lifecycle: {
        onComplete() {
          // @ts-ignore
          props.onOk?.();
        },
        onClose() {
          // @ts-ignore
          props.afterClose?.();
          setTimeout(() => {
            history.go(-1);
          }, 0);
        },
      },
    });
    open();
  },
};

/**
 * MARK: 单个套餐购买，无切换操作
 */
export const SingleBuyDialog = {
  open: async (props) => {
    const user = await checkLogin();

    if (!user.user_id) {
      return;
    }

    if (isGdMerged(user)) {
      HBMessage.error("暂不支持购买");
      return;
    }

    const { SingleBuyDialog: oldBuy } = await import("./buy");
    oldBuy.open(props);
  },
};
/**
 * MARK: 订单继续支付
 */
export const OrderPayDialog = {
  open: async (props) => {
    const user = await checkLogin();

    if (!user.user_id) {
      return;
    }

    if (isGdMerged(user)) {
      HBMessage.error("暂不支持继续支付");
      return;
    }

    const { OrderPayDialog: oldBuy } = await import("./buy");
    oldBuy.open(props);
  },
};

/**
 * MARK: 购买下载席位
 */
export const AddDownloadSeat = {
  async open(props = {}) {
    const user = await checkLogin();
    if (isGdMerged(user)) {
      if (await isBigCompany()) {
        return;
      }

      const { useBusinessModal } = await import("@gdesign/iframe-modal");
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { open } = useBusinessModal({
        baseUrl: "https://" + gaodingEditHost,
        params: {
          type: "Trade",
          tradeType: "ADDITION",
          seatType: "gdmaterial", // 花瓣席位
          config: commonConfig,
        },
        trackerData: getTrackerData(),
        lifecycle: {
          onComplete() {
            // @ts-ignore
            props.onOk?.();
          },
          onClose() {
            // @ts-ignore
            props.afterClose?.();
            setTimeout(() => {
              history.go(-1);
            }, 0);
          },
        },
      });
      open();
    } else {
      HBMessage.error("暂不支持购买");
    }
  },
};

/**稿定优惠券点击使用 */
export const openBuyDialogByCoupon = async (item: ICouponItem) => {
  const tradeType = item.applicable_range_goods_types?.[0];

  let seatType = item.module_codes?.[0];

  const type =
    seatType === ETypes.PlusVip
      ? ETypes.PlusVip
      : tradeType && tradeType !== ETradeTypes.Vip
      ? ETypes.Trade
      : ETypes.Vip;

  if (seatType === ETypes.PlusVip) {
    seatType = ESeatTypes.Gdesign;
  }

  const { useBusinessModal } = await import("@gdesign/iframe-modal");

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { open } = useBusinessModal({
    baseUrl: "https://" + gaodingEditHost,
    params: {
      config: commonConfig,
      type,
      tradeType,
      seatType,
    },
    trackerData: getTrackerData(),
    lifecycle: {
      onComplete() {
        refreshVips();
      },
    },
  });
  open();
};

/**
 * 打开支付挽留弹窗
 */
export const openPayRetention = async ({ params, onOk }) => {
  const { useBusinessModal } = await import("@gdesign/iframe-modal");

  const { banner_img_url, org_class, seat_type, skus, trade_type } = params;

  const type =
    seat_type === ETypes.PlusVip
      ? ETypes.PlusVip
      : trade_type && trade_type !== ETradeTypes.Vip
      ? ETypes.Trade
      : ETypes.Vip;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { open } = useBusinessModal({
    baseUrl: "https://" + gaodingEditHost,
    params: {
      type,
      org_class,
      seatType: seat_type,
      sku_id: skus?.map((item) => item.id).join(","),
      tradeType: trade_type,
      modeType: "Single",
      config: { ...commonConfig, background: banner_img_url },
    },
    trackerData: getTrackerData(),
    lifecycle: {
      onComplete() {
        onOk?.();
      },
    },
  });

  open();
};
